import Vue from "vue";
import { assertSheetsProvider } from "@/api/sheets.provider";
import { sheetsRuntimeProvider } from "@/runtime/sheets-provider";
import {
  isSheetsRequestCancelled,
  normalizeContact,
  normalizeSheet,
  normalizeSheetCollection,
  normalizeSheetColumns,
  normalizeSheetJob,
  normalizeSheetList,
  normalizeSheetRows,
  normalizeSheetSettings,
  normalizeSheetsError,
} from "../sheets.normalizers";

const TERMINAL_JOB_STATES = ["completed", "failed", "cancelled"];

function emptyRowsState() {
  return {
    ids: [],
    cursor: null,
    next_cursor: null,
    total: 0,
    status: "idle",
    error: null,
  };
}

function emptyRequestState() {
  return {
    status: "idle",
    error: null,
  };
}

export function createSheetsState() {
  return {
    scope_profile_id: null,
    sheets_by_id: {},
    sheet_ids: [],
    sheets_list_status: "idle",
    sheets_list_error: null,
    _sheets_list_controller: null,
    _sheets_list_seq: 0,

    active_sheet_id: null,
    active_sheet_status: "idle",
    active_sheet_error: null,
    _active_sheet_controller: null,
    _active_sheet_seq: 0,

    contacts_by_id: {},
    rows_by_sheet: {},
    _rows_controllers: {},
    _rows_seqs: {},

    user_settings_by_sheet: {},
    user_columns_by_sheet: {},
    user_column_versions_by_sheet: {},
    rules_by_sheet: {},
    deliveries_by_sheet: {},
    jobs_by_id: {},
    request_state_by_key: {},
    mutation_state_by_key: {},

    _request_controllers: {},
    _request_seqs: {},
    _request_sheet_ids: {},
    _mutation_seqs: {},
    _job_pollers: {},
    _job_seqs: {},
  };
}

function profileId(rootState) {
  return rootState?.user?.currentId?.profile_id || null;
}

function requestIsCurrent(state, rootState, profile_id) {
  return (
    state.scope_profile_id === profile_id && profileId(rootState) === profile_id
  );
}

function isTerminalJob(job) {
  return TERMINAL_JOB_STATES.includes(job?.state);
}

function toVersion(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function isOlderVersion(incoming, cached) {
  const incoming_version = toVersion(incoming);
  const cached_version = toVersion(cached);
  return (
    incoming_version !== null &&
    cached_version !== null &&
    incoming_version < cached_version
  );
}

function isOlderSnapshot(snapshot, cached) {
  return isOlderVersion(snapshot?.version, cached?.version);
}

const getters = {
  sheets(state) {
    return state.sheet_ids
      .map((sheet_id) => state.sheets_by_id[sheet_id])
      .filter(Boolean);
  },
  active_sheet(state) {
    return state.sheets_by_id[state.active_sheet_id] || null;
  },
  rows_for_sheet: (state) => (sheet_id) => {
    const row_state = state.rows_by_sheet[sheet_id] || emptyRowsState();
    return row_state.ids
      .map((contact_id) => state.contacts_by_id[contact_id])
      .filter(Boolean)
      .map((contact) => ({
        canonical_contact_id: contact.canonical_contact_id,
        version: contact.version,
        field_values: contact.field_values,
        source_crm_object_id: contact.source_crm_object_id ?? null,
        // The legacy Sequences popover still reads sequence_target_id; the bulk
        // action moves to a canonical-aware endpoint with 86eypwgdc.
        sequence_target_id: contact.source_crm_object_id ?? null,
        ...contact.values,
      }));
  },
  active_rows(state, getters) {
    return getters.rows_for_sheet(state.active_sheet_id);
  },
  active_rows_state(state) {
    return state.rows_by_sheet[state.active_sheet_id] || emptyRowsState();
  },
  request_state: (state) => (key) =>
    state.request_state_by_key[key] || emptyRequestState(),
};

const mutations = {
  set_scope(state, profile_id) {
    if (state.scope_profile_id === profile_id) return;
    // A write started under the old profile must stay ignored even after the
    // user comes back, so retire every mutation generation with the context.
    Object.keys(state._mutation_seqs).forEach((key) => {
      Vue.set(state._mutation_seqs, key, state._mutation_seqs[key] + 1);
    });
    state.scope_profile_id = profile_id;
    state.sheets_by_id = {};
    state.sheet_ids = [];
    state.sheets_list_status = "idle";
    state.sheets_list_error = null;
    state.contacts_by_id = {};
    state.rows_by_sheet = {};
    state.user_settings_by_sheet = {};
    state.user_columns_by_sheet = {};
    state.user_column_versions_by_sheet = {};
    state.rules_by_sheet = {};
    state.deliveries_by_sheet = {};
    state.jobs_by_id = {};
    state.request_state_by_key = {};
    state.mutation_state_by_key = {};
    state.active_sheet_id = null;
    state.active_sheet_status = "idle";
    state.active_sheet_error = null;
  },
  set_sheets_list_controller(state, controller) {
    state._sheets_list_controller = controller;
  },
  increment_sheets_list_seq(state) {
    state._sheets_list_seq += 1;
  },
  set_sheets_list_state(state, { status, error = null }) {
    state.sheets_list_status = status;
    state.sheets_list_error = error;
  },
  replace_sheets(state, { sheets_by_id, sheet_ids }) {
    // The list is authoritative for membership, but a write that landed while
    // it was in flight holds the newer copy of an entity.
    const merged = {};
    sheet_ids.forEach((sheet_id) => {
      const incoming = sheets_by_id[sheet_id];
      const current = state.sheets_by_id[sheet_id];
      merged[sheet_id] = isOlderSnapshot(incoming, current)
        ? current
        : { ...current, ...incoming };
    });
    state.sheets_by_id = merged;
    state.sheet_ids = sheet_ids;
  },
  merge_sheet(state, sheet) {
    if (!sheet) return;
    // A metadata read that started before a write can land after it.
    if (isOlderSnapshot(sheet, state.sheets_by_id[sheet.sheet_id])) return;
    Vue.set(state.sheets_by_id, sheet.sheet_id, {
      ...state.sheets_by_id[sheet.sheet_id],
      ...sheet,
    });
    if (!state.sheet_ids.includes(sheet.sheet_id)) {
      state.sheet_ids.push(sheet.sheet_id);
    }
  },
  remove_sheet(state, sheet_id) {
    Vue.delete(state.sheets_by_id, sheet_id);
    state.sheet_ids = state.sheet_ids.filter((id) => id !== sheet_id);
    Vue.delete(state.rows_by_sheet, sheet_id);
    Vue.delete(state.user_settings_by_sheet, sheet_id);
    Vue.delete(state.user_columns_by_sheet, sheet_id);
    Vue.delete(state.user_column_versions_by_sheet, sheet_id);
    Vue.delete(state.rules_by_sheet, sheet_id);
    Vue.delete(state.deliveries_by_sheet, sheet_id);
    if (state.active_sheet_id === sheet_id) {
      state.active_sheet_id = null;
      state.active_sheet_status = "idle";
      state.active_sheet_error = null;
    }
  },
  set_active_sheet_controller(state, controller) {
    state._active_sheet_controller = controller;
  },
  increment_active_sheet_seq(state) {
    state._active_sheet_seq += 1;
  },
  set_active_sheet(state, sheet_id) {
    state.active_sheet_id = sheet_id;
  },
  set_active_sheet_state(state, { status, error = null }) {
    state.active_sheet_status = status;
    state.active_sheet_error = error;
  },
  set_rows_controller(state, { sheet_id, controller }) {
    Vue.set(state._rows_controllers, sheet_id, controller);
  },
  increment_rows_seq(state, sheet_id) {
    Vue.set(state._rows_seqs, sheet_id, (state._rows_seqs[sheet_id] || 0) + 1);
  },
  set_rows_state(state, { sheet_id, status, error = null }) {
    Vue.set(state.rows_by_sheet, sheet_id, {
      ...(state.rows_by_sheet[sheet_id] || emptyRowsState()),
      status,
      error,
    });
  },
  replace_rows(state, { sheet_id, rows }) {
    Object.values(rows.contacts_by_id).forEach((contact) => {
      const current = state.contacts_by_id[contact.canonical_contact_id] || {};
      // A row query that started before a save can resolve after it.
      if (isOlderSnapshot(contact, current)) return;
      Vue.set(state.contacts_by_id, contact.canonical_contact_id, {
        ...current,
        ...contact,
        field_values: { ...(contact.field_values || {}) },
        values: { ...(contact.values || {}) },
      });
    });
    Vue.set(state.rows_by_sheet, sheet_id, {
      ids: rows.ids,
      cursor: rows.cursor,
      next_cursor: rows.next_cursor,
      total: rows.total,
      status: "ready",
      error: null,
    });
  },
  merge_contact(state, contact) {
    if (!contact) return;
    const current = state.contacts_by_id[contact.canonical_contact_id] || {};
    // Contacts are shared between Sheets, so a mutation response can arrive
    // after a newer version of the same contact is already cached.
    if (isOlderSnapshot(contact, current)) return;
    Vue.set(state.contacts_by_id, contact.canonical_contact_id, {
      ...current,
      ...contact,
      field_values: {
        ...(current.field_values || {}),
        ...contact.field_values,
      },
      values: {
        ...(current.values || {}),
        ...contact.values,
      },
    });
  },
  drop_contact_field(state, { contact_id, field_id }) {
    const contact = state.contacts_by_id[contact_id];
    if (!contact?.field_values || !(field_id in contact.field_values)) return;
    const field_values = { ...contact.field_values };
    delete field_values[field_id];
    Vue.set(state.contacts_by_id, contact_id, { ...contact, field_values });
  },
  set_user_settings(state, { sheet_id, settings }) {
    // A settings read that started before a save must not restore the values
    // the save just replaced.
    if (isOlderSnapshot(settings, state.user_settings_by_sheet[sheet_id]))
      return;
    Vue.set(state.user_settings_by_sheet, sheet_id, settings);
  },
  set_user_columns(state, { sheet_id, columns, version = null }) {
    // A column read that started before a save must not restore its version,
    // because the next save sends the cached one.
    if (isOlderVersion(version, state.user_column_versions_by_sheet[sheet_id]))
      return;
    Vue.set(state.user_columns_by_sheet, sheet_id, columns);
    Vue.set(state.user_column_versions_by_sheet, sheet_id, version);
  },
  set_rules(state, { sheet_id, rules }) {
    Vue.set(state.rules_by_sheet, sheet_id, rules);
  },
  set_deliveries(state, { sheet_id, deliveries }) {
    Vue.set(state.deliveries_by_sheet, sheet_id, deliveries);
  },
  set_job(state, job) {
    if (job) Vue.set(state.jobs_by_id, job.job_id, job);
  },
  set_request_controller(state, { key, controller, sheet_id }) {
    Vue.set(state._request_controllers, key, controller);
    Vue.set(state._request_sheet_ids, key, sheet_id);
  },
  remove_request_controller(state, { key, controller }) {
    if (state._request_controllers[key] !== controller) return;
    Vue.delete(state._request_controllers, key);
    Vue.delete(state._request_sheet_ids, key);
  },
  increment_request_seq(state, key) {
    Vue.set(state._request_seqs, key, (state._request_seqs[key] || 0) + 1);
  },
  set_request_state(state, { key, status, error = null }) {
    Vue.set(state.request_state_by_key, key, { status, error });
  },
  increment_job_seq(state, job_id) {
    Vue.set(state._job_seqs, job_id, (state._job_seqs[job_id] || 0) + 1);
  },
  set_job_poller(state, { job_id, timer }) {
    Vue.set(state._job_pollers, job_id, timer);
  },
  remove_job_poller(state, job_id) {
    Vue.delete(state._job_pollers, job_id);
  },
  set_mutation_state(state, { key, status, error = null }) {
    Vue.set(state.mutation_state_by_key, key, { status, error });
  },
  increment_mutation_seq(state, key) {
    Vue.set(state._mutation_seqs, key, (state._mutation_seqs[key] || 0) + 1);
  },
};

async function runMutation(
  context,
  { key, request, normalize, commitResult = () => {} }
) {
  const { state, rootState, commit } = context;
  const profile_id = profileId(rootState);
  commit("set_scope", profile_id);
  commit("increment_mutation_seq", key);
  const mutation_seq = state._mutation_seqs[key];
  context.commit("set_mutation_state", { key, status: "loading" });
  try {
    const response = await request();
    if (
      mutation_seq !== state._mutation_seqs[key] ||
      !requestIsCurrent(state, rootState, profile_id)
    )
      return null;
    const result = normalize ? normalize(response) : response;
    commitResult(result);
    context.commit("set_mutation_state", { key, status: "ready" });
    return result;
  } catch (error) {
    if (
      mutation_seq !== state._mutation_seqs[key] ||
      !requestIsCurrent(state, rootState, profile_id)
    )
      return null;
    const normalized_error = normalizeSheetsError(error);
    context.commit("set_mutation_state", {
      key,
      status: normalized_error.state,
      error: normalized_error,
    });
    throw error;
  }
}

async function runRequest(
  context,
  { key, sheet_id, request, normalize, commitResult }
) {
  const { state, rootState, commit } = context;
  state._request_controllers[key]?.abort();
  const controller = new AbortController();
  const profile_id = profileId(rootState);
  commit("set_scope", profile_id);
  commit("set_request_controller", { key, controller, sheet_id });
  commit("increment_request_seq", key);
  const request_seq = state._request_seqs[key];
  commit("set_request_state", { key, status: "loading" });

  try {
    const response = await request(controller.signal);
    if (
      request_seq !== state._request_seqs[key] ||
      !requestIsCurrent(state, rootState, profile_id)
    )
      return null;
    const result = normalize ? normalize(response) : response;
    commitResult(result);
    commit("set_request_state", { key, status: "ready" });
    return result;
  } catch (error) {
    if (isSheetsRequestCancelled(error)) {
      if (
        request_seq === state._request_seqs[key] &&
        requestIsCurrent(state, rootState, profile_id)
      )
        commit("set_request_state", { key, status: "idle" });
      return null;
    }
    if (
      request_seq !== state._request_seqs[key] ||
      !requestIsCurrent(state, rootState, profile_id)
    )
      return null;
    const normalized_error = normalizeSheetsError(error);
    commit("set_request_state", {
      key,
      status: normalized_error.state,
      error: normalized_error,
    });
    return null;
  } finally {
    commit("remove_request_controller", { key, controller });
  }
}

function cancelSheetRequests(state, commit, sheet_id) {
  if (!sheet_id) return;

  state._rows_controllers[sheet_id]?.abort();
  commit("increment_rows_seq", sheet_id);
  if (state.rows_by_sheet[sheet_id]?.status === "loading") {
    commit("set_rows_state", { sheet_id, status: "idle" });
  }

  Object.entries(state._request_sheet_ids).forEach(
    ([key, request_sheet_id]) => {
      if (request_sheet_id !== sheet_id) return;
      state._request_controllers[key]?.abort();
      commit("increment_request_seq", key);
      commit("set_request_state", { key, status: "idle" });
    }
  );

  Object.entries(state.jobs_by_id).forEach(([job_id, job]) => {
    if (job?.sheet_id !== sheet_id || !state._job_pollers[job_id]) return;
    clearTimeout(state._job_pollers[job_id]);
    commit("increment_job_seq", job_id);
    commit("remove_job_poller", job_id);
  });
}

function invalidatePendingSheetList(context) {
  context.state._sheets_list_controller?.abort();
  context.commit("set_sheets_list_controller", null);
  context.commit("increment_sheets_list_seq");
  if (context.state.sheets_list_status === "loading") {
    context.commit("set_sheets_list_state", { status: "idle" });
  }
}

function createActions(provider) {
  return {
    async load_sheets({ state, rootState, commit }, params = {}) {
      state._sheets_list_controller?.abort();
      const controller = new AbortController();
      const profile_id = profileId(rootState);
      commit("set_scope", profile_id);
      commit("set_sheets_list_controller", controller);
      commit("increment_sheets_list_seq");
      const request_seq = state._sheets_list_seq;
      commit("set_sheets_list_state", { status: "loading" });

      try {
        const response = await provider.getSheets(params, {
          signal: controller.signal,
        });
        if (
          request_seq !== state._sheets_list_seq ||
          !requestIsCurrent(state, rootState, profile_id)
        )
          return null;
        const normalized = normalizeSheetList(response);
        commit("replace_sheets", normalized);
        commit("set_sheets_list_state", { status: "ready" });
        return normalized;
      } catch (error) {
        if (isSheetsRequestCancelled(error)) {
          if (
            request_seq === state._sheets_list_seq &&
            requestIsCurrent(state, rootState, profile_id)
          )
            commit("set_sheets_list_state", { status: "idle" });
          return null;
        }
        if (
          request_seq !== state._sheets_list_seq ||
          !requestIsCurrent(state, rootState, profile_id)
        )
          return null;
        const normalized_error = normalizeSheetsError(error);
        commit("set_sheets_list_state", {
          status: normalized_error.state,
          error: normalized_error,
        });
        return null;
      }
    },

    async open_sheet({ state, rootState, commit }, sheet_id) {
      const previous_sheet_id = state.active_sheet_id;
      if (previous_sheet_id && previous_sheet_id !== sheet_id) {
        cancelSheetRequests(state, commit, previous_sheet_id);
      }
      state._active_sheet_controller?.abort();
      const controller = new AbortController();
      const profile_id = profileId(rootState);
      commit("set_scope", profile_id);
      commit("set_active_sheet_controller", controller);
      commit("increment_active_sheet_seq");
      const request_seq = state._active_sheet_seq;
      commit("set_active_sheet", sheet_id);
      commit("set_active_sheet_state", { status: "loading" });

      try {
        const response = await provider.getSheet(sheet_id, {
          signal: controller.signal,
        });
        if (
          request_seq !== state._active_sheet_seq ||
          state.active_sheet_id !== sheet_id ||
          !requestIsCurrent(state, rootState, profile_id)
        )
          return null;
        const sheet = normalizeSheet(response);
        commit("merge_sheet", sheet);
        commit("set_active_sheet_state", { status: "ready" });
        return sheet;
      } catch (error) {
        if (isSheetsRequestCancelled(error)) {
          if (
            request_seq === state._active_sheet_seq &&
            state.active_sheet_id === sheet_id &&
            requestIsCurrent(state, rootState, profile_id)
          )
            commit("set_active_sheet_state", { status: "idle" });
          return null;
        }
        if (
          request_seq !== state._active_sheet_seq ||
          state.active_sheet_id !== sheet_id ||
          !requestIsCurrent(state, rootState, profile_id)
        )
          return null;
        const normalized_error = normalizeSheetsError(error);
        commit("set_active_sheet_state", {
          status: normalized_error.state,
          error: normalized_error,
        });
        return null;
      }
    },

    async query_sheet_rows(
      { state, rootState, commit },
      { sheet_id, query = {} }
    ) {
      state._rows_controllers[sheet_id]?.abort();
      const controller = new AbortController();
      const profile_id = profileId(rootState);
      commit("set_scope", profile_id);
      commit("set_rows_controller", { sheet_id, controller });
      commit("increment_rows_seq", sheet_id);
      const request_seq = state._rows_seqs[sheet_id];
      commit("set_rows_state", { sheet_id, status: "loading" });

      try {
        const response = await provider.querySheetContacts(sheet_id, query, {
          signal: controller.signal,
        });
        if (
          request_seq !== state._rows_seqs[sheet_id] ||
          !requestIsCurrent(state, rootState, profile_id)
        )
          return null;
        const rows = normalizeSheetRows(response);
        commit("replace_rows", { sheet_id, rows });
        return rows;
      } catch (error) {
        if (isSheetsRequestCancelled(error)) {
          if (
            request_seq === state._rows_seqs[sheet_id] &&
            requestIsCurrent(state, rootState, profile_id)
          )
            commit("set_rows_state", { sheet_id, status: "idle" });
          return null;
        }
        if (
          request_seq !== state._rows_seqs[sheet_id] ||
          !requestIsCurrent(state, rootState, profile_id)
        )
          return null;
        const normalized_error = normalizeSheetsError(error);
        commit("set_rows_state", {
          sheet_id,
          status: normalized_error.state,
          error: normalized_error,
        });
        return null;
      }
    },

    async load_sheet_settings(context, sheet_id) {
      return runRequest(context, {
        key: `settings:${sheet_id}`,
        sheet_id,
        request: (signal) =>
          provider.getSheetUserSettings(sheet_id, { signal }),
        normalize: normalizeSheetSettings,
        commitResult: (settings) =>
          context.commit("set_user_settings", { sheet_id, settings }),
      });
    },
    async load_sheet_columns(context, sheet_id) {
      return runRequest(context, {
        key: `columns:${sheet_id}`,
        sheet_id,
        request: (signal) => provider.getSheetUserColumns(sheet_id, { signal }),
        normalize: normalizeSheetColumns,
        commitResult: ({ columns, version }) =>
          context.commit("set_user_columns", { sheet_id, columns, version }),
      });
    },
    async load_sheet_rules(context, sheet_id) {
      return runRequest(context, {
        key: `rules:${sheet_id}`,
        sheet_id,
        request: (signal) => provider.getSheetRules(sheet_id, { signal }),
        normalize: (response) => normalizeSheetCollection(response, "rules"),
        commitResult: (rules) =>
          context.commit("set_rules", { sheet_id, rules }),
      });
    },
    async load_sheet_deliveries(context, sheet_id) {
      return runRequest(context, {
        key: `deliveries:${sheet_id}`,
        sheet_id,
        request: (signal) => provider.getSheetDeliveries(sheet_id, { signal }),
        normalize: (response) =>
          normalizeSheetCollection(response, "deliveries"),
        commitResult: (deliveries) =>
          context.commit("set_deliveries", { sheet_id, deliveries }),
      });
    },

    async create_sheet(context, { data, idempotency_key }) {
      return runMutation(context, {
        key: "create_sheet",
        request: () => provider.createSheet(data, { idempotency_key }),
        normalize: normalizeSheet,
        commitResult: (sheet) => {
          invalidatePendingSheetList(context);
          context.commit("merge_sheet", sheet);
        },
      });
    },
    async update_sheet(context, { sheet_id, data, idempotency_key }) {
      return runMutation(context, {
        key: `update_sheet:${sheet_id}`,
        request: () =>
          provider.updateSheet(sheet_id, data, { idempotency_key }),
        normalize: normalizeSheet,
        commitResult: (sheet) => context.commit("merge_sheet", sheet),
      });
    },
    async delete_sheet(context, { sheet_id, data, idempotency_key }) {
      return runMutation(context, {
        key: `delete_sheet:${sheet_id}`,
        request: () =>
          provider.deleteSheet(sheet_id, data, { idempotency_key }),
        commitResult: () => {
          // An in-flight list would otherwise reinsert the deleted Sheet.
          invalidatePendingSheetList(context);
          context.commit("remove_sheet", sheet_id);
        },
      });
    },
    async save_sheet_settings(context, { sheet_id, data, idempotency_key }) {
      return runMutation(context, {
        key: `save_settings:${sheet_id}`,
        request: () =>
          provider.saveSheetUserSettings(sheet_id, data, { idempotency_key }),
        normalize: normalizeSheetSettings,
        commitResult: (settings) =>
          context.commit("set_user_settings", { sheet_id, settings }),
      });
    },
    async save_sheet_columns(context, { sheet_id, data, idempotency_key }) {
      const expected_version =
        data.expected_version ??
        context.state.user_column_versions_by_sheet[sheet_id];
      return runMutation(context, {
        key: `save_columns:${sheet_id}`,
        request: () =>
          provider.saveSheetUserColumns(
            sheet_id,
            { ...data, expected_version },
            { idempotency_key }
          ),
        normalize: normalizeSheetColumns,
        commitResult: ({ columns, version }) =>
          context.commit("set_user_columns", { sheet_id, columns, version }),
      });
    },
    async add_membership(context, { sheet_id, data, idempotency_key }) {
      return runMutation(context, {
        key: `add_membership:${sheet_id}`,
        request: () =>
          provider.addSheetMembership(sheet_id, data, { idempotency_key }),
      });
    },
    async remove_membership(
      context,
      { sheet_id, contact_id, data, idempotency_key }
    ) {
      return runMutation(context, {
        key: `remove_membership:${sheet_id}:${contact_id}`,
        request: () =>
          provider.removeSheetMembership(sheet_id, contact_id, data, {
            idempotency_key,
          }),
      });
    },
    async update_contact_field(
      context,
      { sheet_id, contact_id, field_id, data, idempotency_key }
    ) {
      const result = await runMutation(context, {
        key: `update_field:${contact_id}:${field_id}`,
        request: () =>
          provider.updateContactField(sheet_id, contact_id, field_id, data, {
            idempotency_key,
          }),
        normalize: normalizeContact,
        commitResult: (contact) => {
          const cached =
            context.state.contacts_by_id[contact?.canonical_contact_id];
          const rejected = isOlderSnapshot(contact, cached);
          context.commit("merge_contact", contact);
          // The write is authoritative for the field it changed, so a response
          // that answers with the slug value only must not leave the older
          // numeric entry in place.
          if (
            !rejected &&
            contact &&
            !(field_id in (contact.field_values || {}))
          )
            context.commit("drop_contact_field", {
              contact_id: contact.canonical_contact_id,
              field_id,
            });
        },
      });
      if (!result) return result;
      // The merge keeps a newer cached contact, so hand callers that one back
      // rather than the response the store just rejected.
      return context.state.contacts_by_id[result.canonical_contact_id] || result;
    },
    async delete_workspace_contact(
      context,
      { sheet_id, contact_id, data, idempotency_key }
    ) {
      return runMutation(context, {
        key: `delete_contact:${contact_id}`,
        request: () =>
          provider.deleteWorkspaceContact(sheet_id, contact_id, data, {
            idempotency_key,
          }),
      });
    },
    async delete_sheet_field(context, { field_id, data, idempotency_key }) {
      return runMutation(context, {
        key: `delete_field:${field_id}`,
        request: () =>
          provider.deleteSheetField(field_id, data, { idempotency_key }),
      });
    },
    async create_rule(context, { sheet_id, data, idempotency_key }) {
      const result = await runMutation(context, {
        key: `create_rule:${sheet_id}`,
        request: () =>
          provider.createSheetRule(sheet_id, data, { idempotency_key }),
      });
      if (result === null) return null;
      await context.dispatch("load_sheet_rules", sheet_id);
      return result;
    },
    async apply_rule(context, { sheet_id, rule_id, data, idempotency_key }) {
      return runMutation(context, {
        key: `apply_rule:${sheet_id}:${rule_id}`,
        request: () =>
          provider.applySheetRule(sheet_id, rule_id, data, { idempotency_key }),
      });
    },
    async create_import(context, { sheet_id, data, idempotency_key }) {
      return runMutation(context, {
        key: `create_import:${sheet_id}`,
        request: () =>
          provider.createSheetImport(sheet_id, data, { idempotency_key }),
        normalize: normalizeSheetJob,
        commitResult: (job) => context.commit("set_job", job),
      });
    },
    async create_export(context, { sheet_id, data, idempotency_key }) {
      return runMutation(context, {
        key: `create_export:${sheet_id}`,
        request: () =>
          provider.createSheetExport(sheet_id, data, { idempotency_key }),
        normalize: normalizeSheetJob,
        commitResult: (job) => context.commit("set_job", job),
      });
    },
    async refresh_job(context, { sheet_id, job_id, type }) {
      return runRequest(context, {
        key: `job:${job_id}`,
        sheet_id,
        request: (signal) =>
          type === "import"
            ? provider.getSheetImport(sheet_id, job_id, { signal })
            : provider.getSheetExport(sheet_id, job_id, { signal }),
        normalize: normalizeSheetJob,
        commitResult: (job) => context.commit("set_job", job),
      });
    },
    async start_job_polling(
      { state, commit, dispatch },
      { sheet_id, job_id, type, interval = 2000 }
    ) {
      if (state._job_pollers[job_id]) {
        clearTimeout(state._job_pollers[job_id]);
        commit("remove_job_poller", job_id);
      }
      const job = await dispatch("refresh_job", { sheet_id, job_id, type });
      if (!job || isTerminalJob(job)) return job;
      const timer = setTimeout(() => {
        dispatch("start_job_polling", { sheet_id, job_id, type, interval });
      }, interval);
      commit("set_job_poller", { job_id, timer });
      return job;
    },
    stop_job_polling({ state, commit }, job_id) {
      if (state._job_pollers[job_id]) clearTimeout(state._job_pollers[job_id]);
      state._request_controllers[`job:${job_id}`]?.abort();
      commit("increment_request_seq", `job:${job_id}`);
      commit("set_request_state", { key: `job:${job_id}`, status: "idle" });
      commit("increment_job_seq", job_id);
      commit("remove_job_poller", job_id);
    },
    reset_for_profile({ dispatch, commit }, profile_id) {
      dispatch("stop_all_requests");
      commit("set_scope", profile_id);
    },
    stop_all_requests({ state, commit }) {
      state._sheets_list_controller?.abort();
      state._active_sheet_controller?.abort();
      commit("increment_sheets_list_seq");
      commit("increment_active_sheet_seq");
      commit("set_sheets_list_state", { status: "idle" });
      commit("set_active_sheet_state", { status: "idle" });
      Object.entries(state._rows_controllers).forEach(
        ([sheet_id, controller]) => {
          controller?.abort();
          commit("increment_rows_seq", sheet_id);
          commit("set_rows_state", { sheet_id, status: "idle" });
        }
      );
      Object.entries(state._request_controllers).forEach(
        ([key, controller]) => {
          controller?.abort();
          commit("increment_request_seq", key);
          commit("set_request_state", { key, status: "idle" });
        }
      );
      Object.entries(state._job_pollers).forEach(([job_id, timer]) => {
        clearTimeout(timer);
        commit("increment_job_seq", job_id);
        commit("remove_job_poller", job_id);
      });
    },
  };
}

export function createSheetsModule(provider = sheetsRuntimeProvider) {
  assertSheetsProvider(provider);
  return {
    namespaced: true,
    state: createSheetsState(),
    getters,
    mutations,
    actions: createActions(provider),
  };
}

export default createSheetsModule();
